/**
 * Global audio analyser singleton — feeds live frequency data from the
 * Radiojar stream into any subscriber (the DRR header logo, future
 * visualizers, etc.) without every component needing to spin up its own
 * AudioContext / AnalyserNode.
 *
 * Only ONE MediaElementSource can ever be created per <audio> element per
 * page, so we attach the first time an audio element is registered and
 * ignore subsequent attempts.
 *
 * The analyser runs on a single requestAnimationFrame loop that pushes
 * a normalized "bass energy" number (0..1) to every subscriber. That
 * keeps the CPU cost tiny even with multiple visualizers listening.
 */

let audioCtx = null;
let analyser = null;
let dataArray = null;
let sourceAttached = false;
let rafId = null;

// Consumers subscribe with a callback (bass: number 0..1) they want each frame.
const subscribers = new Set();

// Second subscriber pool — receives a Float32Array of N normalized band
// levels per frame (0..1 each). Used by the stereo VU meter etc.
const bandSubscribers = new Set(); // {cb, bands}

/**
 * Attach an <audio> element to the shared analyser. Safe to call multiple
 * times — only the first call wires up the graph. The element MUST have
 * crossOrigin="anonymous" set BEFORE its src loads or Web Audio refuses to
 * decode it (silent failure with all-zero frequency data).
 */
export function attachAudio(el) {
  if (!el || sourceAttached) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    audioCtx = new Ctx();
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 128; // small, cheap — plenty for bass reading
    analyser.smoothingTimeConstant = 0.75;
    dataArray = new Uint8Array(analyser.frequencyBinCount);

    const src = audioCtx.createMediaElementSource(el);
    src.connect(analyser);
    // Also route back to the speakers — createMediaElementSource "steals"
    // the default output otherwise.
    analyser.connect(audioCtx.destination);
    sourceAttached = true;
    startLoop();
  } catch (e) {
    // CORS / autoplay / already-attached — silent, subscribers get 0 energy.
    sourceAttached = false;
  }
}

// Some browsers park the AudioContext in a "suspended" state until the user
// interacts with the page. Call this from a play/pause click handler so the
// analyser wakes up alongside the audio.
export function resumeContext() {
  if (audioCtx && audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {});
  }
}

export function subscribe(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/**
 * Subscribe to band-level frequency data — callback receives a Float32Array
 * of `bands` normalized values (0..1) each frame, from lowest to highest
 * frequency. Perfect for VU meters, spectrum bars, etc.
 */
export function subscribeBands(cb, bands = 8) {
  const entry = { cb, bands, buffer: new Float32Array(bands) };
  bandSubscribers.add(entry);
  if (analyser && rafId == null) startLoop();
  return () => bandSubscribers.delete(entry);
}

function startLoop() {
  if (rafId != null) return;
  const tick = () => {
    if (!analyser || (subscribers.size === 0 && bandSubscribers.size === 0)) {
      // Nothing to do — pause the loop until the next subscriber shows up
      // (checked on subscribe → we restart it there).
      rafId = null;
      return;
    }
    analyser.getByteFrequencyData(dataArray);
    // Bass energy = mean of the first 4 bins (roughly 0-250 Hz on 44.1kHz),
    // normalized to 0..1. Kick drums + bass guitar dominate this band.
    let sum = 0;
    for (let i = 0; i < 4; i++) sum += dataArray[i];
    const bass = Math.min(1, sum / (4 * 255));
    subscribers.forEach((cb) => {
      try {
        cb(bass);
      } catch (e) {
        /* subscriber errors shouldn't kill the loop */
      }
    });
    // Fan-out to band subscribers — split the frequency spectrum into N
    // equal-width bands. Each band's value = mean of its bins, 0..1.
    if (bandSubscribers.size > 0) {
      bandSubscribers.forEach((entry) => {
        const { cb, bands, buffer } = entry;
        const binsPerBand = Math.max(1, Math.floor(dataArray.length / bands));
        for (let b = 0; b < bands; b++) {
          let s = 0;
          const start = b * binsPerBand;
          const end = Math.min(dataArray.length, start + binsPerBand);
          for (let i = start; i < end; i++) s += dataArray[i];
          buffer[b] = s / ((end - start) * 255);
        }
        try {
          cb(buffer);
        } catch (e) {
          /* silent */
        }
      });
    }
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
}

// Kick the loop back on if a subscriber joins while it's parked.
const _origAdd = subscribers.add.bind(subscribers);
subscribers.add = function (cb) {
  const r = _origAdd(cb);
  if (analyser && rafId == null) startLoop();
  return r;
};

// Global CSS variable writer — publishes the smoothed bass value onto
// document.documentElement as `--drr-beat` so any .on-air-dot / brand
// element on the page can react purely through CSS (no per-component ref
// plumbing needed). Attack-fast / decay-slow to match the header logic.
if (typeof document !== "undefined" && document.documentElement) {
  let lastGlobalBass = 0;
  subscribers.add((bass) => {
    const smoothed = bass > lastGlobalBass ? bass : lastGlobalBass * 0.88;
    lastGlobalBass = smoothed;
    document.documentElement.style.setProperty(
      "--drr-beat",
      smoothed.toFixed(3)
    );
  });
}
